import { useState, useEffect, useCallback, useMemo } from 'react';
import Router from 'next/router';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import Autocomplete from '@material-ui/lab/Autocomplete';
import DialogContent from '@material-ui/core/DialogContent';
import getUuidByString from 'uuid-by-string';
import { TextField, FormControl, Button, Grid, CircularProgress } from '@material-ui/core';
import ApiService from '../../api/restApi';
import { useQalert } from '../../utility/services/customHooks';
import { useCustomerInfo } from '../../utility/services/customHooks/auth';
import { useDispatch, useSelector } from 'react-redux';
import { actionTypes } from '../../constants/actionTypes';
import { FormAutoComplete } from '../formElements';
import {
  getCRDetailPagePath,
  LOCAL_NEW_PAGE_DATA,
  getPageDraftDetailPagePath,
} from '../../utility/services/crListHelper';
import { format } from 'date-fns';
import { isEmpty, uniqBy } from 'lodash';
import { getHostNameList, validateUrlWithDomains } from '../../utility/validation/urls';
import { getCountryDetailsByAlpfa2, getCountryDetailsByAlpfa3 } from '../../constants/CountryAndLanguages';
import { COMPONENT_NAMES, SCREEN_NAMES, trackAnalyticsEvent } from '../../constants/analyticsMeta';
import { dispatchMasterDataQuery } from '../../utility/services/api/common';
import {
  PIPELINE_STATUS,
  PIPLINE_TRIGGER_FOR,
  CR_STEPS_PROGRESS,
} from '../../../src/constants/content-recommendations';
import { changeStatusForPageHandler } from '../../utility/cr-common';
import { ANNOTATION_SUB_TYPE_VALUE, ANNOTATION_TYPE } from '../../constants/formMeta/recommendation';
import { useUser } from '@auth0/nextjs-auth0/client';
import { _RESOLUTION_ENTITY_TYPE } from '../../utility/common';

type propsType = {
  defaultPageUrl?: string;
  setShowCreateNewPagePopUp: (params: boolean) => void;
  discoverPagesModalSubmithandler: any;
  isFromDiscoverPagesModal?: boolean;
  domainsListFetched?: any;
};

interface typeCountryData {
  location_code?: string;
  alpha_2?: string;
  alpha_3?: string;
  text?: string;
  value?: string;
  forTempSolu?: number;
}

const DEFAULT_COUNTRY: typeCountryData = {
  text: 'United States of America',
  value: 'United States of America',
  alpha_3: 'USA',
  alpha_2: 'US',
};
interface typeLanguageData {
  country_iso_code?: string;
  language_code?: string;
  // language_name?: string;
  location_code?: string | number | null;
  text?: string;
  value?: string;
  forTempSolu?: number;
}

const DEFAULT_LANGUAGE: typeLanguageData = {
  text: 'English',
  value: 'English',
  language_code: 'en',
  location_code: '2840',
  country_iso_code: 'US',
};

const AddPageToOptimize: React.FunctionComponent<propsType> = ({
  setShowCreateNewPagePopUp,
  defaultPageUrl = '',
  isFromDiscoverPagesModal = false,
  discoverPagesModalSubmithandler,
  domainsListFetched = [],
}) => {
  const [countryDataList, setCountryDataList] = useState<typeCountryData[]>([]);
  const [languageDataList, setLanguageDataList] = useState<typeLanguageData[]>([]);
  const [newPageUrl, setNewPageUrl] = useState('https://');
  const [selectedCountry, setSelectedCountry] = useState<typeCountryData>({ ...DEFAULT_COUNTRY });
  const [selectedLanguage, setSelectedLanguage] = useState({ ...DEFAULT_LANGUAGE });
  const [customerDomain, setCustomerDomain] = useState<string>('');
  const [newPageUrlError, setNewPageUrlError] = useState('');
  const [selectedCountryError, setSelectedCountryError] = useState('');
  const [selectedLanguageError, setSelectedLanguageError] = useState('');
  const [existingPageData, setExistingPageData] = useState<object | null>(null);

  const [validationUrlMessage, setUrlValidationMessage] = useState('');

  const [isApiInProcess, setIsApiInProcess] = useState(false);

  const [, toggleSnackBar] = useQalert();
  const dispatch = useDispatch();
  const masterLanguageAndCountry: { countryData: any[]; languageData: any[] } | object = useSelector(
    (state) => state.root.masterLanguageAndCountry
  );
  const customerInfo = useSelector((state) => state.root.customerInfo) || [];
  const { user } = useUser();
  const custDetails = useCustomerInfo(customerInfo);
  const [newlyAddedPageHaveCRRecords, setNewlyAddedPageHaveCRRecords] = useState(false);
  const [pageLoader, setPageLoader] = useState(false);
  const [openStatusConfirmation, setOpenStatusConfirmation] = useState(false);
  const [openStatusAddNewPageDialog, setOpenStatusAddNewPageDialog] = useState(true);

  useEffect(() => {
    if (defaultPageUrl?.trim()) {
      setNewPageUrl(defaultPageUrl?.trim());
    }
  }, [defaultPageUrl]);

  useEffect(() => {
    if (custDetails?.domain_url) {
      setCustomerDomain(new URL(custDetails?.domain_url)?.hostname);
    }
  }, [custDetails]);

  const cancelHandler = useCallback(() => {
    setShowCreateNewPagePopUp(false);
  }, [setShowCreateNewPagePopUp]);

  const findDuplicateUrl = useCallback(async (pageUrl) => {
    setIsApiInProcess(true);
    try {
      setExistingPageData(null);
      const checkUrlExistOrNot = await ApiService.post(
        `${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/master-data/check-url-exist`,
        {
          pageUrl,
        }
      );
      if (checkUrlExistOrNot && checkUrlExistOrNot?.existingRecord) {
        setExistingPageData(checkUrlExistOrNot?.existingRecord);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('CreateNewPageFromExistingPage:findDuplicateUrl:: error', error);
      throw error;
    } finally {
      setIsApiInProcess(false);
    }
  }, []);

  // const redirectHandler = useCallback((record) => {
  //   const { path, params } =
  //     record?.is_cnp === 'TRUE' ? getPageDraftDetailPagePath(record) : getCRDetailPagePath('', record);
  //   Router.push(path, params);
  // }, []);

  const redirectHandler = useCallback((record) => {
    const { path, params } =
      record?.is_cnp === 'TRUE' ? getPageDraftDetailPagePath(record) : getCRDetailPagePath('', record);
    Router.push(path, params, { shallow: true });
  }, []);

  const existingUrlRedirectHandler = () => {
    redirectHandler(existingPageData);
  };

  const formatCountryForDropDown = useCallback(
    (list) => {
      const newList: typeCountryData[] = list.map((ele) => {
        return { text: ele.NAME, value: ele.NAME, alpha_3: ele.ALPHA_3, alpha_2: ele.ALPHA_2 };
      });
      setCountryDataList(newList);
      let defaultCountry = { ...DEFAULT_COUNTRY };
      if (custDetails?.primary_country) {
        const primaryCustomerCountry = getCountryDetailsByAlpfa3(custDetails?.primary_country);
        if (primaryCustomerCountry) {
          defaultCountry = {
            text: primaryCustomerCountry?.name,
            value: primaryCustomerCountry?.name,
            alpha_3: primaryCustomerCountry?.alpha_3,
            alpha_2: primaryCustomerCountry?.alpha_2,
          };
        }
      }
      setSelectedCountry({ ...defaultCountry });
    },
    [custDetails?.primary_country]
  );

  const formatLanguageForDropDown = useCallback((list) => {
    const newList: typeLanguageData[] = list.map((ele) => {
      return {
        text: ele.LANGUAGE_NAME,
        value: ele.LANGUAGE_NAME,
        language_code: ele.LANGUAGE_CODE,
        location_code: ele.LOCATION_CODE,
        country_iso_code: ele.COUNTRY_ISO_CODE,
      };
    });

    setLanguageDataList(newList);
    setSelectedLanguage({ ...DEFAULT_LANGUAGE });
  }, []);

  useEffect(() => {
    if (!isEmpty(masterLanguageAndCountry)) {
      if (masterLanguageAndCountry['countryData']) {
        formatCountryForDropDown(masterLanguageAndCountry['countryData']);
      }
      if (masterLanguageAndCountry['languageData']) {
        formatLanguageForDropDown(masterLanguageAndCountry['languageData']);
      }
    }
  }, [formatCountryForDropDown, formatLanguageForDropDown, masterLanguageAndCountry]);

  const validateForm = () => {
    let isFormValid = true;
    if (!newPageUrl) {
      isFormValid = false;
      setNewPageUrlError('Please enter URL');
    } else {
      setNewPageUrlError('');
      const allDomains = customerDomain ? [...domainsListFetched, customerDomain] : domainsListFetched;
      const { isValidUrl, errorMessage } = validateUrlWithDomains(newPageUrl, allDomains, true);
      setUrlValidationMessage(errorMessage);
      if (!isValidUrl) {
        isFormValid = false;
      }
    }
    if (!selectedCountry?.value || !selectedCountry?.alpha_3) {
      isFormValid = false;
      setSelectedCountryError('Please select country');
    } else {
      setSelectedCountryError('');
    }
    if (!selectedLanguage?.language_code || !selectedLanguage?.location_code || !selectedLanguage?.value) {
      isFormValid = false;
      setSelectedLanguageError('Please select language');
    } else {
      setSelectedLanguageError('');
    }
    return isFormValid;
  };

  const onSubmitHandler = async () => {
    const isFormValid = validateForm();
    let updatedNewPageUrl = newPageUrl;
    if (newPageUrl?.endsWith('/')) {
      updatedNewPageUrl = newPageUrl?.replace(/(?<!:)(\/)+/g, '$1');
      setNewPageUrl(updatedNewPageUrl);
    }

    try {
      let existingRecord = false;
      if (isFormValid) {
        existingRecord = await findDuplicateUrl(updatedNewPageUrl);
      }
      if (isFormValid && !existingRecord && custDetails) {
        setIsApiInProcess(true);
        // eslint-disable-next-line
        const locationCodeBasedOnCountry: any = getCountryDetailsByAlpfa2(selectedCountry?.alpha_2);
        const additional_page_info = {
          country: selectedCountry,
          language: { ...selectedLanguage, location_code: null },
        };
        if (locationCodeBasedOnCountry) {
          // we are setuping the location code based on the selection of county not direclty using it selected langauge info
          additional_page_info.language.location_code = locationCodeBasedOnCountry?.location_code;
          additional_page_info.country.location_code = locationCodeBasedOnCountry?.location_code;
        }
        let dataSource = Router?.router?.query?.type || Router?.router?.query?.source;
        dataSource = dataSource === 'paid' ? 'PAID' : 'ORG';
        const payload = {
          content_recommendation_id: getUuidByString(`${custDetails.cust_code}_${updatedNewPageUrl}`),
          page: updatedNewPageUrl,
          data_source: dataSource,
          additional_page_info,
        };
        // const response: { data: any[] } = await ApiService.post(
        //   `${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/recommendations/content-recommendations/create-page`,
        //   payload
        // );

        const [response] = await Promise.all([
          ApiService.post(
            `${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/recommendations/content-recommendations/create-page`,
            payload
          ),
          // changeStatusForPageHandler({
          //   pageId: payload?.content_recommendation_id,
          //   annotationSubtype: ANNOTATION_SUB_TYPE_VALUE.RESEARCH_KEYWORDS,
          //   isFromContentBriefScreen: false,
          // }),
        ]);
        let pageStatusChangeResponse = {};
        const dateValue = new Date();
        const annotationPayload = {
          recommendation_id: payload?.content_recommendation_id,
          reason: ANNOTATION_SUB_TYPE_VALUE.RESEARCH_KEYWORDS,
          date: format(new Date(dateValue), 'MM/dd/yyyy'),
          entity_type: _RESOLUTION_ENTITY_TYPE.CONTENT_RECOMMENDATIONS,
          entity_id: payload?.content_recommendation_id,
          annotation_type: ANNOTATION_TYPE.RESOLUTION,
          annotation_subtype: ANNOTATION_SUB_TYPE_VALUE.RESEARCH_KEYWORDS,
          annotation_details: {},
        };
        try {
          const result = await ApiService.post(
            `${process.env.NEXT_PUBLIC_QUATTR_API_URL}/api/utility/annotation`,
            annotationPayload
          );
          pageStatusChangeResponse = { ...(result?.data || {}), reason: ANNOTATION_SUB_TYPE_VALUE.RESEARCH_KEYWORDS };
        } catch (error) {
          console.error('AddPageToOptimize :: changeStatusForPageHandler:: error', error);
        }
        if (response) {
          trackAnalyticsEvent({
            eventFor: 'button',
            buttonText: 'form submit',
            counterName: 'Pages Added to Optimize',
            isAddCount: true,
            user,
            payload: {
              label: 'Add New Page',
              customer_url: newPageUrl,
              component_name: COMPONENT_NAMES.content_recommendations,
              screen_name: SCREEN_NAMES.content_recommendations,
            },
          });
          const newPageRecord = { ...LOCAL_NEW_PAGE_DATA, ...(pageStatusChangeResponse || {}) };
          newPageRecord.uuid = payload.content_recommendation_id;
          newPageRecord.data_source = payload.data_source;
          newPageRecord.finalurl = payload.page;
          // TODO legacy code, will the string really always be present? should we add `|| ''` ?
          newPageRecord.site = custDetails.domain_url as string;
          newPageRecord.created_date = format(new Date(), 'MM/dd/yyyy');
          dispatch({
            type: actionTypes.SET_NEW_PAGE_CONTENT_RECOMMENDATION_DATA,
            payload: newPageRecord,
          });
          dispatch({
            type: actionTypes.ADD_NEW_PAGE_IN_CR_FILTERS,
            page: updatedNewPageUrl,
          });

          //// THIS IS TO RESET WHEN PAGE WILL OPEN FROM GT FOR CREATION
          dispatch({
            type: actionTypes.ADD_PAGE_TO_OPTIMIZE_FORM_BOX,
            page: {},
          });
          redirectHandler(newPageRecord);
        }
      }
    } catch (e) {
      setIsApiInProcess(false);
      toggleSnackBar({ type: 'error', text: 'Oops! Something Went Wrong. Please try again.', open: true });
      console.error('Add new page :: Api Error :: ', e);
    }
    // setIsApiInProcess(false);
  };

  const uniqueLanguageDataList = useMemo(() => {
    return uniqBy(languageDataList, 'text') || [];
  }, [languageDataList]);

  const uniqueCountryDataList = useMemo(() => {
    return uniqBy(countryDataList, 'alpha_2') || [];
  }, [countryDataList]);

  const newPageForm = () => {
    return (
      <div className="create-new-page-popup">
        {!isFromDiscoverPagesModal ? (
          <Autocomplete
            data-testid="new-added-chips-button"
            // multiple
            id="multi-value"
            options={[]}
            value={newPageUrl}
            // inputValue={inputValue}
            freeSolo
            onInputChange={(event, newInputValue) => {
              setNewPageUrl(newInputValue);
            }}
            // ChipProps={{
            //   className: 'multi-chip-setting',
            // }}
            disableClearable={true}
            renderInput={(params) => (
              <>
                <TextField
                  {...params}
                  className="multiAdd"
                  variant="filled"
                  label={'Enter URL'}
                  fullWidth
                  name="value"
                  InputProps={{ ...params.InputProps, disableUnderline: true }}
                  //   error={showError}
                />
              </>
            )}
          />
        ) : null}
        {validationUrlMessage?.trim() ? <p className="m-0 mt-5 new-page-form-error">{validationUrlMessage}</p> : null}
        <p className="m-0 mt-5 new-page-form-error">{newPageUrlError}</p>
        {existingPageData ? (
          <p className="url-exist-message">
            Page already exists
            <span className="redirect-click-btn" onClick={existingUrlRedirectHandler}>
              {' '}
              click here{' '}
            </span>
            to view
          </p>
        ) : null}
        <Grid
          container
          style={{ alignItems: 'center', justifyContent: 'start' }}
          className="mt-15 mb-12"
          data-testid="country-selection"
        >
          <FormControl variant="outlined" style={{ width: '100%' }} data-testid="select-country-test">
            <FormAutoComplete
              // id={id}
              value={selectedCountry}
              options={uniqueCountryDataList}
              label={'Select Country'}
              className={'mb-25 w-100'}
              onChange={(value) => {
                setSelectedCountry(value);
              }}
              onTextBlur={(value) => {
                const findCountry: typeCountryData = uniqueCountryDataList?.find(
                  (country) => country?.text === value || country?.value === value
                ) || {
                  forTempSolu: new Date().getTime(), // NOTE:: as once null,undefined,empty set, secondtime Comp is not going re-render due to Virtal DOM check, as first and second time value is same, so do not re-render, to avoid, i have added this solu
                };
                setSelectedCountry(findCountry);
              }}
              fullWidth
              disableClearable
            />
          </FormControl>
          <p className="m-0 mt-5 new-page-form-error">{selectedCountryError}</p>
        </Grid>
        <Grid
          container
          style={{ alignItems: 'center', justifyContent: 'start' }}
          className="mt-15 mb-12"
          data-testid="language-selection"
        >
          <FormControl variant="outlined" style={{ width: '100%' }}>
            <FormAutoComplete
              // id={id}
              value={selectedLanguage}
              options={uniqueLanguageDataList}
              label={'Select Language'}
              className={'mb-25 w-100'}
              onChange={(value) => {
                setSelectedLanguage(value);
              }}
              onTextBlur={(value) => {
                const findLang: typeLanguageData = uniqueLanguageDataList?.find(
                  (lang) => lang?.text === value || lang?.value === value
                ) || {
                  forTempSolu: new Date().getTime(), // NOTE:: as once null,undefined,empty set, secondtime Comp is not going re-render due to Virtal DOM check, as first and second time value is same, so do not re-render, to avoid, i have added this solu
                };

                setSelectedLanguage(findLang);
              }}
              fullWidth
              disableClearable
            />
          </FormControl>
          <p className="m-0 mt-5 new-page-form-error">{selectedLanguageError}</p>
        </Grid>
        <Grid container style={{ alignItems: 'center', justifyContent: 'start' }} className="mt-15 mb-12">
          <Grid item xs={12} sm={12} className="align-self-center">
            <div style={{ float: 'right' }}>
              <span style={{ marginRight: '8px' }}>
                <Button
                  className={`create-keyword-cancel-btn`}
                  variant="outlined"
                  color="primary"
                  onClick={cancelHandler}
                  style={{ marginTop: '8px' }}
                  data-testid="cancelBtn"
                >
                  Cancel
                </Button>
                <Button
                  className={`create-intent-cta`}
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    isFromDiscoverPagesModal
                      ? discoverPagesModalSubmithandler(selectedCountry, selectedLanguage)
                      : onSubmitHandler();
                  }}
                  style={{ marginTop: '8px', textTransform: 'capitalize', boxShadow: 'none' }}
                  data-testid="addpageBtn"
                  disabled={isApiInProcess || pageLoader}
                >
                  {isApiInProcess || pageLoader ? <CircularProgress className="position-abs" size={20} /> : null}
                  {isFromDiscoverPagesModal ? 'Add Page To Optimize' : 'Add New Page'}
                </Button>
              </span>
            </div>
          </Grid>
        </Grid>
      </div>
    );
  };

  return (
    <>
      {openStatusAddNewPageDialog && (
        <Dialog
          PaperProps={{
            style: {
              width: 500,
              maxWidth: 500,
              borderRadius: '4px',
              marginTop: '-140px',
            },
          }}
          onClose={() => {}}
          aria-labelledby="simple-dialog-title"
          className={''}
          maxWidth="sm"
          open={true}
          data-testid="addNewKeywordDlgBoxWrapper"
        >
          {
            <DialogTitle
              className="new-keyword-dialog-title"
              style={{ padding: '10px 0px 0px 25px' }}
              data-testid="addNewKeywordDlgTitle"
            >
              {isFromDiscoverPagesModal ? 'Add Page To Optimize' : 'Add New Page'}
            </DialogTitle>
          }
          <DialogContent className={''} data-testid="addNewKeywordDlgContent">
            {newPageForm()}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default AddPageToOptimize;
